import React, { useState } from 'react';
import {
  DollarSign, Users, Zap, AlertTriangle, RefreshCw, Play,
  CheckCircle, XCircle, Info, X, Shield
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { useToast } from '../contexts/ToastContext';
import './Settlement.css';

interface SingleSettleResult {
  amount_settled?: number;
  status?: string;
  message?: string;
  user_id?: number;
  [key: string]: any;
}

interface BatchSettleResult {
  users_settled?: number;
  total_amount?: number;
  failures?: number;
  failed_users?: any[];
  message?: string;
  [key: string]: any;
}

const Settlement: React.FC = () => {
  const toast = useToast();

  const [userId, setUserId] = useState('');
  const [forceSettle, setForceSettle] = useState(false);
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleError, setSingleError] = useState<string | null>(null);
  const [singleResult, setSingleResult] = useState<SingleSettleResult | null>(null);

  const [minCredits, setMinCredits] = useState(100);
  const [batchSize, setBatchSize] = useState(50);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchResult, setBatchResult] = useState<BatchSettleResult | null>(null);

  const [showSingleConfirm, setShowSingleConfirm] = useState(false);
  const [showBatchConfirm, setShowBatchConfirm] = useState(false);

  const handleSingleSettle = async () => {
    setShowSingleConfirm(false);
    const parsedId = parseInt(userId, 10);
    if (isNaN(parsedId) || parsedId <= 0) {
      setSingleError('Please enter a valid positive User ID');
      toast.error('Invalid Input', 'User ID must be a positive number');
      return;
    }

    setSingleLoading(true);
    setSingleError(null);
    setSingleResult(null);

    try {
      const result = await adminService.settleUser(parsedId, forceSettle);
      setSingleResult(result);
      toast.success(
        'Settlement Complete',
        `User #${parsedId} settled successfully`
      );
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err.message || 'Settlement failed';
      setSingleError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error('Settlement Failed', typeof msg === 'string' ? msg : 'An error occurred');
    } finally {
      setSingleLoading(false);
    }
  };

  const handleBatchSettle = async () => {
    setShowBatchConfirm(false);
    setBatchLoading(true);
    setBatchError(null);
    setBatchResult(null);

    try {
      const result = await adminService.batchSettle({
        min_credits: minCredits,
        batch_size: batchSize,
      });
      setBatchResult(result);
      const settledCount = result?.users_settled ?? result?.settled_count ?? 0;
      toast.success(
        'Batch Settlement Complete',
        `${settledCount} user(s) settled successfully`
      );
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err.message || 'Batch settlement failed';
      setBatchError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error('Batch Settlement Failed', typeof msg === 'string' ? msg : 'An error occurred');
    } finally {
      setBatchLoading(false);
    }
  };

  const openSingleConfirm = () => {
    const parsedId = parseInt(userId, 10);
    if (isNaN(parsedId) || parsedId <= 0) {
      setSingleError('Please enter a valid positive User ID');
      return;
    }
    setSingleError(null);
    setShowSingleConfirm(true);
  };

  return (
    <div className="settlement-page">
      {/* Header */}
      <div className="stl-header">
        <div className="header-content">
          <h1><Zap size={28} /> Settlement Engine</h1>
          <p className="header-subtitle">
            Convert user earned credits and TP into wallet balances. This is a sensitive financial operation — proceed with care.
          </p>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="stl-cards-grid">
        {/* Single User Settlement */}
        <div className="stl-card">
          <div className="stl-card-header">
            <div className="stl-card-icon single-icon">
              <DollarSign size={22} />
            </div>
            <div>
              <h2>Single User Settlement</h2>
              <p>Settle one user's earned credits to their wallet</p>
            </div>
          </div>

          <div className="stl-card-body">
            <div className="stl-form-group">
              <label htmlFor="settle-user-id">User ID</label>
              <input
                id="settle-user-id"
                type="number"
                min="1"
                placeholder="Enter user ID (e.g. 1042)"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={singleLoading}
              />
            </div>

            <div className="stl-checkbox-group">
              <label className="stl-checkbox-label">
                <input
                  type="checkbox"
                  checked={forceSettle}
                  onChange={(e) => setForceSettle(e.target.checked)}
                  disabled={singleLoading}
                />
                <span className="stl-checkbox-text">
                  <strong>Force settle</strong>
                  <small>Override minimum threshold requirements</small>
                </span>
              </label>
              {forceSettle && (
                <div className="stl-force-warning">
                  <AlertTriangle size={14} />
                  <span>Force mode bypasses safety checks. Use with caution.</span>
                </div>
              )}
            </div>

            <button
              className="stl-action-btn primary-btn"
              onClick={openSingleConfirm}
              disabled={singleLoading || !userId.trim()}
            >
              {singleLoading ? (
                <>
                  <RefreshCw size={18} className="spin" />
                  Settling...
                </>
              ) : (
                <>
                  <Play size={18} />
                  Settle User
                </>
              )}
            </button>

            {singleError && (
              <div className="stl-inline-error">
                <XCircle size={16} />
                <span>{singleError}</span>
              </div>
            )}

            {singleResult && (
              <div className="stl-result success">
                <div className="stl-result-header">
                  <CheckCircle size={18} />
                  <strong>Settlement Result</strong>
                </div>
                <div className="stl-result-details">
                  {singleResult.amount_settled !== undefined && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Amount Settled</span>
                      <span className="stl-result-value highlight">
                        {Number(singleResult.amount_settled).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {singleResult.status && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Status</span>
                      <span className={`stl-result-badge ${singleResult.status}`}>{singleResult.status}</span>
                    </div>
                  )}
                  {singleResult.message && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Message</span>
                      <span className="stl-result-value">{singleResult.message}</span>
                    </div>
                  )}
                  {singleResult.user_id && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">User ID</span>
                      <span className="stl-result-value">#{singleResult.user_id}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Batch Settlement */}
        <div className="stl-card">
          <div className="stl-card-header">
            <div className="stl-card-icon batch-icon">
              <Users size={22} />
            </div>
            <div>
              <h2>Batch Settlement</h2>
              <p>Process settlements for multiple qualifying users</p>
            </div>
          </div>

          <div className="stl-card-body">
            <div className="stl-warning-banner">
              <AlertTriangle size={18} />
              <div>
                <strong>Financial Operation Warning</strong>
                <p>This will process real financial transactions. Settled amounts are credited to user wallets and cannot be easily reversed.</p>
              </div>
            </div>

            <div className="stl-form-row">
              <div className="stl-form-group">
                <label htmlFor="min-credits">Minimum Credits Threshold</label>
                <input
                  id="min-credits"
                  type="number"
                  min="0"
                  placeholder="100"
                  value={minCredits}
                  onChange={(e) => setMinCredits(Number(e.target.value))}
                  disabled={batchLoading}
                />
                <small>Only settle users with at least this many credits</small>
              </div>

              <div className="stl-form-group">
                <label htmlFor="batch-size">Batch Size</label>
                <input
                  id="batch-size"
                  type="number"
                  min="1"
                  max="500"
                  placeholder="50"
                  value={batchSize}
                  onChange={(e) => setBatchSize(Number(e.target.value))}
                  disabled={batchLoading}
                />
                <small>Maximum number of users to process</small>
              </div>
            </div>

            <button
              className="stl-action-btn batch-btn"
              onClick={() => setShowBatchConfirm(true)}
              disabled={batchLoading}
            >
              {batchLoading ? (
                <>
                  <RefreshCw size={18} className="spin" />
                  Processing Batch...
                </>
              ) : (
                <>
                  <Zap size={18} />
                  Run Batch Settlement
                </>
              )}
            </button>

            {batchError && (
              <div className="stl-inline-error">
                <XCircle size={16} />
                <span>{batchError}</span>
              </div>
            )}

            {batchResult && (
              <div className="stl-result success">
                <div className="stl-result-header">
                  <CheckCircle size={18} />
                  <strong>Batch Settlement Result</strong>
                </div>
                <div className="stl-result-details">
                  {(batchResult.users_settled !== undefined || batchResult.settled_count !== undefined) && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Users Settled</span>
                      <span className="stl-result-value highlight">
                        {batchResult.users_settled ?? (batchResult as any).settled_count ?? 0}
                      </span>
                    </div>
                  )}
                  {batchResult.total_amount !== undefined && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Total Amount</span>
                      <span className="stl-result-value highlight">
                        {Number(batchResult.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {(batchResult.failures !== undefined && batchResult.failures > 0) && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Failures</span>
                      <span className="stl-result-value error-text">{batchResult.failures}</span>
                    </div>
                  )}
                  {batchResult.message && (
                    <div className="stl-result-row">
                      <span className="stl-result-label">Message</span>
                      <span className="stl-result-value">{batchResult.message}</span>
                    </div>
                  )}
                  {batchResult.failed_users && batchResult.failed_users.length > 0 && (
                    <div className="stl-failed-list">
                      <span className="stl-result-label">Failed Users</span>
                      <div className="stl-failed-items">
                        {batchResult.failed_users.map((f: any, i: number) => (
                          <div key={i} className="stl-failed-item">
                            <XCircle size={14} />
                            <span>User #{f.user_id ?? f.id ?? '?'}: {f.reason ?? f.error ?? 'Unknown error'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settlement History Placeholder */}
      <div className="stl-history-section">
        <div className="stl-history-header">
          <h2><Shield size={20} /> Settlement History</h2>
        </div>
        <div className="stl-history-placeholder">
          <Info size={40} />
          <h3>Settlement history coming soon</h3>
          <p>A complete audit log of all settlement operations will be available in a future update.</p>
        </div>
      </div>

      {/* Single Settle Confirmation Modal */}
      {showSingleConfirm && (
        <div className="modal-overlay" onClick={() => setShowSingleConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><AlertTriangle size={20} className="modal-warning-icon" /> Confirm Settlement</h3>
              <button className="modal-close" onClick={() => setShowSingleConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="stl-confirm-warning">
                <Shield size={20} />
                <span>This is a financial operation</span>
              </div>
              <p className="confirm-text">
                You are about to settle credits for <strong>User #{userId}</strong>.
                {forceSettle && (
                  <>
                    {' '}Force mode is <strong className="force-highlight">enabled</strong> — minimum thresholds will be bypassed.
                  </>
                )}
              </p>
              <p className="confirm-text secondary">
                The user's earned credits/TP will be converted to their wallet balance. This action processes a real financial transaction.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSingleConfirm(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleSingleSettle} className="confirm-settle-btn">
                <DollarSign size={18} />
                Confirm &amp; Settle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Settle Confirmation Modal */}
      {showBatchConfirm && (
        <div className="modal-overlay" onClick={() => setShowBatchConfirm(false)}>
          <div className="modal-content confirm-modal batch-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><AlertTriangle size={20} className="modal-warning-icon" /> Confirm Batch Settlement</h3>
              <button className="modal-close" onClick={() => setShowBatchConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="stl-confirm-warning critical">
                <AlertTriangle size={20} />
                <span>Batch financial operation — please review carefully</span>
              </div>
              <p className="confirm-text">
                This will settle credits for up to <strong>{batchSize} users</strong> who have at least <strong>{minCredits} credits</strong>.
              </p>
              <div className="stl-confirm-params">
                <div className="stl-confirm-param">
                  <span className="param-label">Min Credits</span>
                  <span className="param-value">{minCredits}</span>
                </div>
                <div className="stl-confirm-param">
                  <span className="param-label">Batch Size</span>
                  <span className="param-value">{batchSize}</span>
                </div>
              </div>
              <p className="confirm-text secondary">
                Each qualifying user's earned credits/TP will be converted to their wallet balance. These are real financial transactions that will be difficult to reverse.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBatchConfirm(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleBatchSettle} className="confirm-settle-btn batch">
                <Zap size={18} />
                Run Batch Settlement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settlement;
